import React, { useMemo } from "react";
import { Card, Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import useFetchWithAuth from "../../../features/hooks/useFetchWithAuth";

const WEEK_LABELS = ["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "НД"];

const toMinutes = (value) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const parts = trimmed.split("-").map((piece) => piece.trim());
  if (parts.length !== 2) return null;

  const parsePart = (part) => {
    const [hoursRaw, minutesRaw = "0"] = part.split(":");
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return hours * 60 + minutes;
  };

  const start = parsePart(parts[0]);
  const end = parsePart(parts[1]);
  if (start == null || end == null) return null;
  const diff = end - start;
  return diff > 0 ? diff : null;
};

const isMakeupShift = (shiftValue) => {
  const trimmed = String(shiftValue || "").trim();
  if (!trimmed) return false;
  return /відпрац/i.test(trimmed) || /^[vbв]\b|^[vbв]\s*-/i.test(trimmed);
};

const planHoursFromShift = (shiftValue) => {
  if (isMakeupShift(shiftValue)) return 0;
  const minutes = toMinutes(shiftValue);
  if (!minutes) return null;
  return minutes / 60;
};

const parseNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const formatHoursMinutes = (value) => {
  const totalMinutes = Math.round(Math.abs(value) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours} год ${minutes} хв`;
};

const getWeekStart = (date) => {
  const day = date.day();
  const diff = (day + 6) % 7;
  return date.subtract(diff, "day");
};

const WorkChart = ({ userId }) => {
  const { data, loading } = useFetchWithAuth("/statistic/schedule/");

  const scheduleMap = useMemo(() => {
    if (!Array.isArray(data) || !userId) return {};
    return data.reduce((acc, entry) => {
      const entryUser =
        typeof entry.user === "object" && entry.user !== null
          ? entry.user.id
          : entry.user;
      if (entryUser !== userId) return acc;
      acc[entry.date] = entry;
      return acc;
    }, {});
  }, [data, userId]);

  const { totalDelta, hours, colors, label } = useMemo(() => {
    const today = dayjs().startOf("day");
    const weekStart = getWeekStart(today);
    const weekDays = Array.from({ length: 7 }, (_, i) =>
      weekStart.add(i, "day")
    );

    let totalDeltaHours = 0;
    const nextHours = [];
    const nextColors = [];

    weekDays.forEach((dayValue) => {
      const key = dayValue.format("YYYY-MM-DD");
      const entry = scheduleMap[key];
      const planHours = planHoursFromShift(entry?.shift ?? "");
      const factValue = parseNumber(entry?.fact_people);
      const isFuture = dayValue.isAfter(today, "day");
      const isToday = dayValue.isSame(today, "day");

      if (planHours == null || isFuture) {
        nextHours.push(0);
        nextColors.push("var(--darkCardBg)");
        return;
      }

      const countedFact =
        factValue != null ? factValue : isToday ? null : 0;

      if (countedFact != null) {
        totalDeltaHours += countedFact - planHours;
      }

      const diff =
        countedFact == null ? 0 : countedFact - planHours;
      nextHours.push(planHours);
      nextColors.push(
        countedFact == null
          ? "var(--darkCardBg)"
          : diff > 0
          ? "var(--infoColor)"
          : diff < 0
          ? "var(--dangerColor)"
          : "var(--successColor)"
      );
    });

    let title = "Баланс годин";
    if (totalDeltaHours < 0) title = "Недопрацьовані години";
    if (totalDeltaHours > 0) title = "Перероблені години";

    return {
      totalDelta: totalDeltaHours,
      hours: nextHours,
      colors: nextColors,
      label: title,
    };
  }, [scheduleMap]);

  if (loading) {
    return (
      <Card
        sx={{
          borderRadius: 4,
          p: 4,
          minHeight: 350,
          height: 350,
          boxShadow: "none",
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }} style={{ fontWeight: 700 }}>
          Графік роботи
        </Typography>
        <Typography sx={{ color: "text.secondary" }}>Завантаження...</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 4,
        p: 4,
        minHeight: 350,
        height: 350,
        boxShadow: "none",
      }}
    >
      <Typography
        variant="h5"
        sx={{ mb: 2 }}
        style={{ fontWeight: 700, fontSize: "20px" }}
      >
        Графік роботи
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box
          sx={{
            width: 43,
            height: 43,
            borderRadius: "10%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
            ".dark-theme &": { background: "var(--darkSecondaryColor)" },
          }}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="11"
              cy="11"
              r="10"
              stroke="var(--primaryColor)"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M11 6V11L14 13"
              stroke="var(--primaryColor)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 400, fontSize: 16, mb: 0.5 }}>
            {label}
          </Typography>
          <Typography
            variant="h5"
            sx={{ mb: 2 }}
            style={{ fontWeight: 700, fontSize: "20px" }}
          >
            {formatHoursMinutes(totalDelta)}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          height: 160,
          mt: 2,
          mb: 1,
        }}
      >
        {WEEK_LABELS.map((day, i) => (
          <Box
            key={day}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: 36,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: hours[i] * 16,
                background: colors[i],
                borderRadius: 0,
                mb: 1,
                transition: "background 0.2s",
              }}
            />
            <Typography
              sx={{
                color: "var(--bodyColor)",
                fontWeight: i === 4 ? 700 : 400,
                fontSize: 15,
                textAlign: "center",
                letterSpacing: 0,
              }}
            >
              {day}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export default WorkChart;
